import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiBox,
  FiCalendar,
  FiCheck,
  FiDownload,
  FiMapPin,
  FiPackage,
  FiTruck,
  FiUser,
  FiX,
} from "react-icons/fi";
import { FaMoneyBillWave } from "react-icons/fa";
import { RiScales2Line } from "react-icons/ri";
import API_URL from "../config/config";
import CancelOrderDialog from "./CancelOrderDialog";
import {
  canCancelOrderNow,
  getCancellationWindowRemainingMs,
} from "../utils/orderFlow";
import { getStoredUserName, getStoredUserPhone } from "../utils/sessionUser";
import { getInvoiceApiUrl } from "../utils/invoiceApi";

const ORDER_ICON = "/assets/logo/logo.png";
const COMPANY_DETAILS = {
  name: "Instaport Delivery",
  officeAddress:
    "R/3 A Wing Ground Floor, Mass Metropolis, Chembur, Mumbai, Maharashtra 400071",
  phone: "+91 88501 66601",
  email: "support@instaportdelivery.com",
  gst: "27AALFI9841C1Z6",
};

const ORDER_DETAILS_TIMEOUT_MS = 20000;
const inflightOrderDetailsRequests = new Map();

const formatAmount = (amount) => `\u20B9${Math.round(Number(amount) || 0)}`;

const formatDateTime = (value) =>
  new Date(value || Date.now()).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const formatPaymentLabel = (method) => {
  const value = String(method || "").toLowerCase();
  if (value === "cod") return "Cash on Delivery";
  if (value === "wallet") return "Wallet";
  if (value === "online") return "Online";
  if (value === "upi") return "UPI";
  if (value === "card") return "Card";
  return method || "Unknown";
};

const getStatusLabel = (status, orderDetails) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "delivered") return "Delivered";
  if (normalized === "cancelled") return "Cancelled";
  if (normalized === "processing") return "Out for Delivery";
  if (normalized === "new") return "Placed";
  if (!orderDetails?.rider) return "Looking for Rider";
  return status || "Unknown";
};

const getStatusClassName = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "delivered") return "bg-[#eef9f0] text-[#138536]";
  if (normalized === "cancelled") return "bg-[#fff1f2] text-[#dc2626]";
  if (normalized === "processing") return "bg-[#fff4e6] text-[#f59e0b]";
  return "bg-[#f3f4f6] text-[#475569]";
};

const formatFullPhoneNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";

  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }

  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }

  if (digits.length === 13 && digits.startsWith("091")) {
    return `+91 ${digits.slice(3, 8)} ${digits.slice(8)}`;
  }

  return String(value || "");
};

const getLocationContact = (location) =>
  formatFullPhoneNumber(
    location?.phone_number || location?.mobileno || location?.contact || ""
  );

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const titleFromStatusMessage = (message = "") => {
  if (message.includes("Pickup Started")) return "Pickup Started";
  if (message.includes("Arrived at Pickup")) return "Arrived at Pickup";
  if (message.includes("Pickup Closed")) return "Picked Up";
  if (message.includes("Drop Closed") || message.includes("Drop Point Closed")) {
    return "Delivered to Drop";
  }
  if (message.toLowerCase().includes("delivered")) return "Delivered";
  if (message.toLowerCase().includes("assigned")) return "Rider Assigned";
  return message || "Status Updated";
};

const buildTimeline = (orderDetails) => {
  const baseItems = [
    {
      title: "Placed",
      time: orderDetails?.time_stamp,
      detail: "Order has been placed successfully",
    },
  ];

  const dynamicItems = (orderDetails?.orderStatus || []).map((item) => ({
    title: titleFromStatusMessage(item?.message),
    time: item?.timestamp || orderDetails?.time_stamp,
    detail: item?.address || item?.message || "Order updated",
  }));

  const merged = [...baseItems, ...dynamicItems];
  const seen = new Set();

  return merged.filter((item) => {
    const key = `${item.title}-${item.time}-${item.detail}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const OrderDetails = ({ order, onClose, onOrderUpdated }) => {
  const [orderDetails, setOrderDetails] = useState(order || {});
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCancelRestrictionDialog, setShowCancelRestrictionDialog] =
    useState(false);
  const [cancelWindowRefreshKey, setCancelWindowRefreshKey] = useState(0);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const navigate = useNavigate();
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchOrderDetailsFromApi = useCallback(
    async (token) => {
      const candidateUrls = [
        `${API_URL}/order/customer_app/${order._id}`,
        `${API_URL}/order/customer/${order._id}`,
      ];
      const dedupedUrls = [...new Set(candidateUrls)];
      const requestKey = String(order?._id || "");

      if (!requestKey) {
        throw new Error("Invalid order data");
      }

      if (inflightOrderDetailsRequests.has(requestKey)) {
        return inflightOrderDetailsRequests.get(requestKey);
      }

      const requestPromise = Promise.any(
        dedupedUrls.map(async (url) => {
          const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: ORDER_DETAILS_TIMEOUT_MS,
          });
          const data = response.data;
          if (!data?.error && data?.order) {
            return data.order;
          }
          throw new Error(data?.message || "Failed to fetch order details");
        })
      )
        .catch((error) => {
          if (Array.isArray(error?.errors)) {
            const authError = error.errors.find((item) => item?.response?.status === 401);
            if (authError) throw authError;

            const timeoutError = error.errors.find((item) => item?.code === "ECONNABORTED");
            if (timeoutError) throw timeoutError;

            const firstMeaningfulError = error.errors.find(Boolean);
            if (firstMeaningfulError) throw firstMeaningfulError;
          }
          throw error;
        })
        .finally(() => {
          inflightOrderDetailsRequests.delete(requestKey);
        });

      inflightOrderDetailsRequests.set(requestKey, requestPromise);
      return requestPromise;
    },
    [order?._id]
  );

  const refreshOrderDetails = useCallback(
    async ({ showLoader = false, showErrorToast = true } = {}) => {
      if (!order?._id) {
        toast.error("Invalid order data");
        onClose?.();
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please sign in again.");
        navigate("/");
        return;
      }

      if (showLoader) {
        setLoading(true);
      }

      try {
        const resolvedOrder = await fetchOrderDetailsFromApi(token);
        if (!isMountedRef.current) return;
        setOrderDetails(resolvedOrder || {});
      } catch (error) {
        if (!isMountedRef.current) return;
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          toast.error("Session expired. Please sign in again.");
          navigate("/");
        } else if (showErrorToast) {
          toast.error(
            error.response?.data?.message ||
              error.message ||
              "Error fetching order details"
          );
        }
        setOrderDetails({});
      } finally {
        if (showLoader && isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [fetchOrderDetailsFromApi, navigate, order?._id]
  );

  useEffect(() => {
    refreshOrderDetails({ showLoader: true });
  }, [refreshOrderDetails]);

  useEffect(() => {
    if (!order?._id) return undefined;

    const socket = io(API_URL.current, {
      transports: ["websocket", "polling"],
    });

    const joinOrderRoom = () => {
      socket.emit("track_order", { orderId: order._id });
    };

    const handleOrderStatusUpdate = (payload = {}) => {
      if (String(payload.orderId || "") !== String(order._id)) return;

      setOrderDetails((currentOrderDetails) => ({
        ...currentOrderDetails,
        status: payload.currentStatus || currentOrderDetails?.status,
        orderStatus: Array.isArray(payload.completedStops)
          ? payload.completedStops
          : currentOrderDetails?.orderStatus || [],
        updatedAt: payload.updatedAt || new Date().toISOString(),
      }));

    };

    socket.on("connect", joinOrderRoom);
    socket.on("order_status_update", handleOrderStatusUpdate);

    if (socket.connected) {
      joinOrderRoom();
    }

    return () => {
      socket.off("connect", joinOrderRoom);
      socket.off("order_status_update", handleOrderStatusUpdate);
      socket.disconnect();
    };
  }, [onOrderUpdated, order?._id, refreshOrderDetails]);

  useEffect(() => {
    const remainingMs = getCancellationWindowRemainingMs(orderDetails);
    if (!remainingMs) return undefined;

    const timer = window.setTimeout(() => {
      setCancelWindowRefreshKey((current) => current + 1);
    }, remainingMs + 200);

    return () => window.clearTimeout(timer);
  }, [cancelWindowRefreshKey, orderDetails]);

  const pickup = orderDetails?.locations?.[0];
  const drop = orderDetails?.locations?.[1];
  const timeline = useMemo(() => buildTimeline(orderDetails), [orderDetails]);
  const hasPricingEditRestriction =
    Number(orderDetails?.pointsUsed || 0) > 0 ||
    Number(orderDetails?.coupon_discount_amount || 0) > 0 ||
    String(orderDetails?.coupon_code || "").trim() !== "";

  const canEditOrder =
    orderDetails?.status !== "delivered" &&
    orderDetails?.status !== "cancelled" &&
    (orderDetails?.orderStatus?.length || 0) < 3 &&
    !hasPricingEditRestriction;
  const canCancelCurrentOrder = canCancelOrderNow(orderDetails);

  const handleEditOrder = () => {
    if (!orderDetails?._id) return;
    if (hasPricingEditRestriction) {
      toast.error(
        "Orders with coupon or points applied cannot be edited. Please cancel this order instead."
      );
      return;
    }
    navigate(`/edit-order/${orderDetails._id}`);
  };

  const handleCancelClick = () => {
    if (hasPricingEditRestriction) {
      setShowCancelRestrictionDialog(true);
      return;
    }
    setShowCancelDialog(true);
  };

  // Retained only as a reference for the former browser-print layout. The
  // active action below downloads the canonical server-generated PDF.
  // eslint-disable-next-line no-unused-vars
  const legacyHandleDownloadInvoice = () => {
    const invoiceWindow = window.open("", "_blank", "width=960,height=720");
    if (!invoiceWindow) {
      toast.error("Please allow popups to download the invoice.");
      return;
    }

    const customerName =
      orderDetails?.customer?.fullname ||
      orderDetails?.fullname ||
      getStoredUserName();
    const customerPhone =
      orderDetails?.customer?.mobileno ||
      orderDetails?.mobileno ||
      getStoredUserPhone();
    const riderName =
      orderDetails?.rider?.fullname ||
      orderDetails?.rider?.name ||
      orderDetails?.riderName ||
      "Not Assigned";
    const riderId =
      orderDetails?.rider?.IPID ||
      orderDetails?.rider?.ipid ||
      orderDetails?.rider?.id ||
      orderDetails?.rider?._id ||
      orderDetails?.riderId ||
      "N/A";
    const riderVehicleNumber =
      orderDetails?.rider?.vehicle_number ||
      orderDetails?.rider?.vehicleNumber ||
      orderDetails?.rider?.vehicle_no ||
      orderDetails?.rider?.vehicleno ||
      orderDetails?.rider?.bike_number ||
      orderDetails?.vehicle_number ||
      "N/A";
    const riderPhone =
      orderDetails?.rider?.mobileno ||
      orderDetails?.rider?.phone ||
      orderDetails?.rider?.contact ||
      "N/A";
    const safePickupText = escapeHtml(pickup?.text || "N/A");
    const safeDropText = escapeHtml(drop?.text || "N/A");
    const safeCustomerName = escapeHtml(customerName || "N/A");
    const safeCustomerPhone = escapeHtml(customerPhone || "N/A");
    const safeRiderName = escapeHtml(riderName);
    const safeRiderId = escapeHtml(riderId);
    const safeRiderVehicleNumber = escapeHtml(riderVehicleNumber);
    const safeRiderPhone = escapeHtml(riderPhone);

    invoiceWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${orderDetails?.orderId || ""}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 32px; color: #1f2937; background: #f8fafc; }
            h1, h2, h3, p { margin: 0; }
            .invoice-shell { max-width: 980px; margin: 0 auto; background: #ffffff; border-radius: 26px; padding: 30px; box-shadow: 0 18px 60px rgba(15, 23, 42, 0.10); }
            .header { display:flex; justify-content:space-between; align-items:flex-start; gap: 24px; margin-bottom:24px; padding-bottom: 22px; border-bottom: 1px solid #e5e7eb; }
            .header-right { text-align: right; }
            .grid { display:grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
            .card { border:1px solid #e5e7eb; border-radius:16px; padding:16px; background: #fff; }
            .muted { color:#6b7280; font-size:12px; text-transform:uppercase; letter-spacing:.08em; }
            .value { margin-top:8px; font-size:20px; font-weight:700; }
            .amount { color:#11843a; font-size:36px; font-weight:800; }
            .timeline-item { display:grid; grid-template-columns: 160px 1fr; gap: 16px; padding:12px 0; border-top:1px solid #e5e7eb; }
            .company-block { margin-top: 8px; color:#4b5563; font-size: 14px; line-height: 1.65; }
            .summary-grid { display:grid; grid-template-columns: 1.15fr .85fr; gap: 16px; margin-bottom: 24px; }
            .label-pair { margin-top: 12px; }
            .footer-note { margin-top: 24px; border-top: 1px solid #e5e7eb; padding-top: 18px; color:#6b7280; font-size: 13px; line-height: 1.7; }
            .pill { display:inline-flex; align-items:center; padding:8px 14px; border-radius:999px; background:#eef9f0; color:#138536; font-weight:700; margin-top:8px; }
            @media print {
              body { background: #fff; padding: 0; }
              .invoice-shell { box-shadow: none; border-radius: 0; max-width: none; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-shell">
            <div class="header">
              <div>
                <h1 style="font-size: 34px;">${escapeHtml(COMPANY_DETAILS.name)} Invoice</h1>
                <p style="margin-top:8px;color:#6b7280;">Order #${escapeHtml(orderDetails?.orderId || "N/A")}</p>
                <div class="company-block">
                  <div>${escapeHtml(COMPANY_DETAILS.officeAddress)}</div>
                  <div>Phone: ${escapeHtml(COMPANY_DETAILS.phone)} | Email: ${escapeHtml(COMPANY_DETAILS.email)}</div>
                  <div>GST No.: ${escapeHtml(COMPANY_DETAILS.gst)}</div>
                </div>
              </div>
              <div class="header-right">
                <img src="${window.location.origin + ORDER_ICON}" alt="logo" style="height:56px;" />
                <div class="label-pair">
                  <div class="muted">Invoice Date</div>
                  <div class="value" style="font-size:16px;">${formatDateTime(Date.now())}</div>
                </div>
              </div>
            </div>
            <div class="summary-grid">
              <div class="card">
                <div class="muted">Order Amount</div>
                <div class="amount">${formatAmount(orderDetails?.amount)}</div>
                <div class="label-pair">
                  <div class="muted">Customer</div>
                  <div class="value">${safeCustomerName}</div>
                  <div style="margin-top:4px;font-size:16px;color:#4b5563;">${safeCustomerPhone}</div>
                </div>
              </div>
              <div class="card">
                <div class="muted">Payment Method</div>
                <div class="value">${escapeHtml(formatPaymentLabel(orderDetails?.payment_method))}</div>
                <div class="muted" style="margin-top:16px;">Status</div>
                <div class="pill">${escapeHtml(getStatusLabel(orderDetails?.status, orderDetails))}</div>
                <div class="muted" style="margin-top:16px;">Order Date & Time</div>
                <div class="value" style="font-size:16px;">${formatDateTime(orderDetails?.time_stamp)}</div>
              </div>
            </div>
            <div class="grid">
              <div class="card">
                <div class="muted">Pickup Address</div>
                <div class="value" style="font-size:18px;font-weight:500;line-height:1.6;">${safePickupText}</div>
                <div class="label-pair">
                  <div class="muted">Pickup Contact</div>
                    <div class="value" style="font-size:16px;font-weight:600;white-space:nowrap;overflow-wrap:anywhere;">${escapeHtml(
                      getLocationContact(pickup) || "N/A"
                    )}</div>
                </div>
              </div>
              <div class="card">
                <div class="muted">Drop Address</div>
                <div class="value" style="font-size:18px;font-weight:500;line-height:1.6;">${safeDropText}</div>
                <div class="label-pair">
                  <div class="muted">Drop Contact</div>
                    <div class="value" style="font-size:16px;font-weight:600;white-space:nowrap;overflow-wrap:anywhere;">${escapeHtml(
                      getLocationContact(drop) || "N/A"
                    )}</div>
                </div>
              </div>
              <div class="card">
                <div class="muted">Rider Details</div>
                <div class="value" style="font-size:18px;">${safeRiderName}</div>
                <div class="label-pair">
                  <div class="muted">Rider ID</div>
                  <div class="value" style="font-size:16px;font-weight:600;">${safeRiderId}</div>
                </div>
                <div class="label-pair">
                  <div class="muted">Vehicle Number</div>
                  <div class="value" style="font-size:16px;font-weight:600;">${safeRiderVehicleNumber}</div>
                </div>
                <div class="label-pair">
                  <div class="muted">Rider Contact</div>
                  <div class="value" style="font-size:16px;font-weight:600;">${safeRiderPhone}</div>
                </div>
              </div>
              <div class="card">
                <div class="muted">Office / Billing Details</div>
                <div class="value" style="font-size:18px;">${escapeHtml(COMPANY_DETAILS.name)}</div>
                <div style="margin-top:10px;font-size:15px;line-height:1.7;color:#4b5563;">
                  ${escapeHtml(COMPANY_DETAILS.officeAddress)}<br />
                  Phone: ${escapeHtml(COMPANY_DETAILS.phone)}<br />
                  Email: ${escapeHtml(COMPANY_DETAILS.email)}<br />
                  GST No.: ${escapeHtml(COMPANY_DETAILS.gst)}
                </div>
              </div>
            </div>
            <div class="card">
              <h3 style="margin-bottom:12px;">Order Timeline</h3>
              ${timeline
                .map(
                  (item) => `
                    <div class="timeline-item">
                      <div>${formatDateTime(item.time)}</div>
                      <div>
                        <div style="font-weight:700;">${escapeHtml(item.title)}</div>
                        <div style="margin-top:4px;color:#6b7280;">${escapeHtml(item.detail)}</div>
                      </div>
                    </div>
                  `
                )
                .join("")}
            </div>
            <div class="footer-note">
              This invoice is generated digitally by ${escapeHtml(
                COMPANY_DETAILS.name
              )}. For billing support, reach us at ${escapeHtml(
                COMPANY_DETAILS.phone
              )} or ${escapeHtml(COMPANY_DETAILS.email)}.
            </div>
          </div>
          <script>
            window.onload = () => { window.print(); };
          </script>
        </body>
      </html>
    `);
    invoiceWindow.document.close();
  };

  const handleDownloadInvoice = async () => {
    if (isDownloadingInvoice) return;
    if (String(orderDetails?.status || "").toLowerCase() !== "delivered") {
      toast.info("Invoice is available after delivery is completed.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token || !orderDetails?._id) {
      toast.error("Please sign in again to download your invoice.");
      return;
    }

    setIsDownloadingInvoice(true);
    try {
      const response = await axios.get(
        `${getInvoiceApiUrl()}/invoice/customer/${encodeURIComponent(
          orderDetails._id
        )}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          timeout: 30000,
        }
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Instaport-Invoice-${orderDetails.orderId || "order"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Invoice downloaded successfully.");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Unable to generate your invoice right now. Please try again.";
      toast.error(message);
    } finally {
      if (isMountedRef.current) setIsDownloadingInvoice(false);
    }
  };

  const customerName =
    orderDetails?.customer?.fullname ||
    orderDetails?.fullname ||
    getStoredUserName();
  const customerPhone =
    orderDetails?.customer?.mobileno ||
    orderDetails?.mobileno ||
    getStoredUserPhone();
  const riderNameRaw =
    orderDetails?.rider?.fullname ||
    orderDetails?.rider?.name ||
    orderDetails?.riderName ||
    "";
  const riderIdRaw =
    orderDetails?.rider?.IPID ||
    orderDetails?.rider?.ipid ||
    orderDetails?.rider?.id ||
    orderDetails?.rider?._id ||
    orderDetails?.riderId ||
    "";
  const riderVehicleNumberRaw =
    orderDetails?.rider?.vehicle_number ||
    orderDetails?.rider?.vehicleNumber ||
    orderDetails?.rider?.vehicle_no ||
    orderDetails?.rider?.vehicleno ||
    orderDetails?.rider?.bike_number ||
    orderDetails?.vehicle_number ||
    "";
  const riderPhoneRaw =
    orderDetails?.rider?.mobileno ||
    orderDetails?.rider?.phone ||
    orderDetails?.rider?.contact ||
    "";
  const normalizedOrderStatus = String(orderDetails?.status || "").toLowerCase();
  const hasAssignedRider = Boolean(
    riderNameRaw || riderIdRaw || riderVehicleNumberRaw || riderPhoneRaw
  );
  const showCompactRiderCard =
    hasAssignedRider &&
    (normalizedOrderStatus === "delivered" ||
      normalizedOrderStatus === "cancelled");
  const showFullRiderCard =
    hasAssignedRider &&
    normalizedOrderStatus !== "delivered" &&
    normalizedOrderStatus !== "cancelled";
  const showRiderCard = showCompactRiderCard || showFullRiderCard;
  const riderName = riderNameRaw || "Not Assigned";
  const riderId = riderIdRaw || "N/A";
  const riderVehicleNumber = riderVehicleNumberRaw || "N/A";
  const riderPhone = riderPhoneRaw || "N/A";

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[rgba(20,25,35,0.38)] px-2 py-2 backdrop-blur-[3px] sm:px-3 sm:py-4">
      <div className="relative max-h-[96vh] w-full max-w-[860px] overflow-hidden rounded-[22px] bg-[linear-gradient(180deg,#ffffff_0%,#fffef9_100%)] shadow-[0_26px_70px_rgba(15,23,42,0.18)] sm:max-h-[90vh] sm:rounded-[28px]">
        <div className="max-h-[96vh] overflow-y-auto px-3 pb-4 pt-3 sm:max-h-[90vh] sm:px-5 sm:pb-5 sm:pt-4">
          <div className="absolute inset-x-0 top-0 h-[120px] bg-[radial-gradient(circle_at_55%_0%,rgba(235,248,236,0.95),rgba(255,255,255,0)_60%)]" />
          <div className="absolute right-16 top-6 hidden h-20 w-28 rounded-[22px] bg-[radial-gradient(circle_at_20%_20%,rgba(209,240,215,0.9),rgba(255,255,255,0)_68%)] lg:block" />

          <div className="relative flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full border border-[#cde8d2] bg-[#f6fff7] text-[#11843a] sm:h-[54px] sm:w-[54px]">
                <FiBox className="text-[20px] sm:text-[24px]" />
              </div>
              <div>
                <h2 className="text-[18px] font-bold tracking-[-0.03em] text-[#1f2432] sm:text-[22px]">
                  Order Details
                </h2>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-[#eef0f3] bg-white text-[#303645] shadow-[0_10px_24px_rgba(30,41,59,0.08)] transition hover:bg-[#f8fafc] sm:h-[44px] sm:w-[44px]"
            >
              <FiX className="text-[18px] sm:text-[20px]" />
            </button>
          </div>

          {loading ? (
            <div className="flex h-[420px] items-center justify-center">
              <div className="h-14 w-14 animate-spin rounded-full border-[4px] border-[#f4c75b] border-t-[#f59e0b]" />
            </div>
          ) : Object.keys(orderDetails).length === 0 ? (
            <div className="py-20 text-center text-sm text-[#667186]">
              Failed to load order details.
            </div>
          ) : (
            <>
              <div className="relative mt-4 grid gap-3 lg:grid-cols-[1fr_1.5fr]">
                <div className="rounded-[16px] border border-[#eae4d8] bg-white px-4 py-4 shadow-[0_10px_28px_rgba(170,146,103,0.08)] sm:rounded-[18px] sm:px-5 sm:py-5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#697386]">
                    Order ID
                  </p>
                  <p className="mt-2 break-all text-[18px] font-bold tracking-[-0.03em] text-[#1d2433] sm:text-[22px]">
                    #{orderDetails?.orderId || "N/A"}
                  </p>

                  <p className="mt-5 text-[11px] font-medium uppercase tracking-[0.08em] text-[#697386]">
                    Order Amount
                  </p>
                  <p className="mt-2 text-[28px] font-extrabold leading-none text-[#0c8f36] sm:text-[34px]">
                    {formatAmount(orderDetails?.amount)}
                  </p>
                </div>

                <div className="grid gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-[16px] border border-[#eae4d8] bg-white px-4 py-4 shadow-[0_10px_28px_rgba(170,146,103,0.08)] sm:rounded-[18px] sm:px-5 sm:py-5">
                      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#697386]">
                        Status
                      </p>
                      <div className="mt-3">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[12px] font-semibold sm:px-4 sm:text-[14px] ${getStatusClassName(
                            orderDetails?.status
                          )}`}
                        >
                          <FiCheck className="text-[14px]" />
                          {getStatusLabel(orderDetails?.status, orderDetails)}
                        </span>
                      </div>
                    </div>

                    <div className="rounded-[16px] border border-[#eae4d8] bg-white px-4 py-4 shadow-[0_10px_28px_rgba(170,146,103,0.08)] sm:rounded-[18px] sm:px-5 sm:py-5">
                      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#697386]">
                        Payment Method
                      </p>
                      <div className="mt-3 flex items-center gap-2.5 text-[14px] font-semibold text-[#1f2432] sm:text-[16px]">
                        <span className="flex h-9 w-9 items-center justify-center rounded-[12px] bg-[#eef9f0] text-[#108234]">
                          <FaMoneyBillWave className="text-[16px]" />
                        </span>
                        {formatPaymentLabel(orderDetails?.payment_method)}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[16px] border border-[#eae4d8] bg-white px-4 py-4 shadow-[0_10px_28px_rgba(170,146,103,0.08)] sm:rounded-[18px] sm:px-5 sm:py-5">
                    <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#697386]">
                      Date &amp; Time
                    </p>
                    <div className="mt-3 flex items-center gap-2.5 text-[14px] font-semibold text-[#1f2432] sm:text-[16px]">
                      <span className="flex h-9 w-9 items-center justify-center rounded-[12px] bg-[#f7f9fc] text-[#273249]">
                        <FiCalendar className="text-[16px]" />
                      </span>
                      {formatDateTime(orderDetails?.time_stamp)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative mt-4 grid gap-3 lg:grid-cols-[1fr_76px_1fr] lg:items-center">
                <div className="group overflow-hidden rounded-[22px] border border-[#d9ebdc] bg-[linear-gradient(145deg,#ffffff_0%,#f7fff9_100%)] px-4 py-4 shadow-[0_14px_36px_rgba(17,132,58,0.10)] sm:px-5 sm:py-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] bg-[linear-gradient(180deg,#13a03f_0%,#0c8a33_100%)] text-white shadow-[0_12px_24px_rgba(17,132,58,0.22)]">
                      <FiMapPin className="text-[19px]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#e9f9ed] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#11843a]">
                          Pickup Point
                        </span>
                      </div>
                      <h3 className="mt-3 text-[18px] font-bold tracking-[-0.03em] text-[#1f2432] sm:text-[20px]">
                        Pickup Address
                      </h3>
                      <p className="mt-3 text-[14px] leading-7 text-[#3e4a5f] sm:text-[15px]">
                        {pickup?.text || "N/A"}
                      </p>
                      {getLocationContact(pickup) && (
                        <div className="mt-4 inline-flex max-w-full items-center rounded-full border border-[#cde8d2] bg-white/90 px-4 py-2 text-[13px] font-semibold text-[#11843a] shadow-[0_8px_20px_rgba(17,132,58,0.08)] sm:text-[14px]">
                          Contact: {getLocationContact(pickup)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="relative hidden justify-center lg:flex">
                  <div className="absolute left-1/2 top-1/2 h-[2px] w-full -translate-x-1/2 -translate-y-1/2 border-t-2 border-dashed border-[#8ed199]" />
                  <div className="relative flex h-14 w-14 items-center justify-center rounded-full border border-[#9fddb0] bg-[radial-gradient(circle_at_35%_35%,#ffffff_0%,#edfff1_45%,#daf8e2_100%)] text-[#138536] shadow-[0_12px_30px_rgba(17,132,58,0.14)]">
                    <FiTruck className="text-[22px]" />
                  </div>
                </div>

                <div className="group overflow-hidden rounded-[22px] border border-[#d9ebdc] bg-[linear-gradient(145deg,#ffffff_0%,#f7fff9_100%)] px-4 py-4 shadow-[0_14px_36px_rgba(17,132,58,0.10)] sm:px-5 sm:py-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] bg-[linear-gradient(180deg,#13a03f_0%,#0c8a33_100%)] text-white shadow-[0_12px_24px_rgba(17,132,58,0.22)]">
                      <FiMapPin className="text-[19px]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#e9f9ed] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#11843a]">
                          Drop Point
                        </span>
                      </div>
                      <h3 className="mt-3 text-[18px] font-bold tracking-[-0.03em] text-[#1f2432] sm:text-[20px]">
                        Drop Address
                      </h3>
                      <p className="mt-3 text-[14px] leading-7 text-[#3e4a5f] sm:text-[15px]">
                        {drop?.text || "N/A"}
                      </p>
                      {getLocationContact(drop) && (
                        <div className="mt-4 inline-flex max-w-full items-center rounded-full border border-[#cde8d2] bg-white/90 px-4 py-2 text-[13px] font-semibold text-[#11843a] shadow-[0_8px_20px_rgba(17,132,58,0.08)] sm:text-[14px]">
                          Contact: {getLocationContact(drop)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <div className="rounded-[20px] border border-[#f3e1b9] bg-[linear-gradient(135deg,#fffdf7_0%,#fff8ea_100%)] px-4 py-4 shadow-[0_12px_30px_rgba(233,179,53,0.10)] sm:px-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] border border-[#ffd99d] bg-white text-[#f5a10a] shadow-[0_8px_18px_rgba(245,161,10,0.10)]">
                      <FiPackage className="text-[18px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#697386]">
                        Package Type
                      </p>
                      <p className="mt-2 text-[26px] font-bold tracking-[-0.03em] text-[#1f2432]">
                        {orderDetails?.package || "Documents"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-[20px] border border-[#f3e1b9] bg-[linear-gradient(135deg,#fffdf7_0%,#fff8ea_100%)] px-4 py-4 shadow-[0_12px_30px_rgba(233,179,53,0.10)] sm:px-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] border border-[#ffd99d] bg-white text-[#f5a10a] shadow-[0_8px_18px_rgba(245,161,10,0.10)]">
                      <RiScales2Line className="text-[18px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#697386]">
                        Package Weight
                      </p>
                      <p className="mt-2 text-[26px] font-bold tracking-[-0.03em] text-[#1f2432]">
                        {orderDetails?.parcel_weight || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`mt-4 grid gap-3 ${showRiderCard ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
                <div className="h-full overflow-hidden rounded-[20px] border border-[#dceee3] bg-[linear-gradient(180deg,#ffffff_0%,#f7fef9_100%)] shadow-[0_14px_32px_rgba(17,132,58,0.08)]">
                  <div className="border-b border-[#e7f4ea] bg-[linear-gradient(90deg,rgba(17,132,58,0.08)_0%,rgba(255,255,255,0)_100%)] px-4 py-3 sm:px-5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#697386]">
                      Customer
                    </p>
                  </div>
                  <div className="flex items-center gap-3 px-4 py-5 sm:px-5">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] border border-[#cde8d2] bg-[#f6fff7] text-[#11843a]">
                      <FiUser className="text-[18px]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[20px] font-bold tracking-[-0.03em] text-[#1f2432]">
                        {customerName || "User"}
                      </p>
                      <p className="mt-2 break-all text-[14px] text-[#334155] sm:text-[15px]">
                        {customerPhone || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {showRiderCard && (
                  <div className="h-full overflow-hidden rounded-[20px] border border-[#dceee3] bg-[linear-gradient(180deg,#ffffff_0%,#f7fef9_100%)] shadow-[0_14px_32px_rgba(17,132,58,0.08)]">
                    <div className="border-b border-[#e7f4ea] bg-[linear-gradient(90deg,rgba(17,132,58,0.08)_0%,rgba(255,255,255,0)_100%)] px-4 py-3 sm:px-5">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#697386]">
                        Rider Details
                      </p>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-5 sm:px-5">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] border border-[#cde8d2] bg-[#f6fff7] text-[#11843a]">
                        <FiTruck className="text-[18px]" />
                      </span>
                      <div className="min-w-0 flex-1">
                        {showCompactRiderCard ? (
                          <p className="min-w-0 text-[20px] font-bold tracking-[-0.03em] text-[#1f2432]">
                            {riderName}
                          </p>
                        ) : (
                          <>
                            <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-start">
                              <p className="min-w-0 text-[20px] font-bold tracking-[-0.03em] text-[#1f2432]">
                                {riderName}
                              </p>
                              <p className="text-left text-[15px] font-semibold text-[#5b677a] sm:text-right">
                                {riderId}
                              </p>
                            </div>
                            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-start">
                              <p className="break-all text-[14px] text-[#334155] sm:text-[15px]">
                                {riderPhone}
                              </p>
                              <p className="break-all text-left text-[14px] font-semibold text-[#11843a] sm:text-right sm:text-[15px]">
                                {riderVehicleNumber}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {(canEditOrder || canCancelCurrentOrder) && (
                <div className="mt-4 flex justify-end gap-2.5">
                  {canEditOrder && (
                    <button
                      type="button"
                      onClick={handleEditOrder}
                      className="rounded-[12px] border border-[#d8e0ea] px-4 py-2.5 text-[13px] font-semibold text-[#334155] transition hover:bg-[#f8fafc]"
                    >
                      Edit Order
                    </button>
                  )}
                  {canCancelCurrentOrder && (
                    <button
                      type="button"
                      onClick={handleCancelClick}
                      className="rounded-[12px] border border-[#ffd3d7] bg-[#fff5f6] px-4 py-2.5 text-[13px] font-semibold text-[#e11d48] transition hover:bg-[#ffecee]"
                    >
                      Cancel Order
                    </button>
                  )}
                </div>
              )}

              {hasPricingEditRestriction &&
                orderDetails?.status !== "delivered" &&
                orderDetails?.status !== "cancelled" && (
                  <p className="mt-3 text-sm text-[#667085]">
                    Orders with coupon or points applied can only be cancelled. Editing is disabled for this order.
                  </p>
                )}

              <div className="mt-4 rounded-[18px] border border-[#e9e2d5] bg-white px-4 py-4 shadow-[0_10px_28px_rgba(170,146,103,0.08)] sm:rounded-[20px] sm:px-5 sm:py-5">
                <h3 className="text-[15px] font-semibold uppercase tracking-[0.02em] text-[#1f2432] sm:text-[17px]">
                  Order Timeline
                </h3>

                <div className="mt-4">
                  {timeline.map((item, index) => (
                    <div
                      key={`${item.title}-${item.time}-${index}`}
                      className={`grid grid-cols-[24px_1fr] gap-3 py-3 sm:grid-cols-[28px_170px_1fr] sm:gap-4 sm:py-4 ${
                        index !== timeline.length - 1 ? "border-b border-[#edf0f4]" : ""
                      }`}
                    >
                      <div className="relative flex justify-center">
                        <span className="relative z-10 flex h-6 w-6 items-center justify-center rounded-full bg-[#0a8f2f] text-white">
                          <FiCheck className="text-[12px]" />
                        </span>
                        {index !== timeline.length - 1 && (
                          <span className="absolute top-6 h-[calc(100%+12px)] w-[2px] bg-[#0a8f2f]" />
                        )}
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold leading-tight text-[#1f2432] sm:text-[16px]">
                          {item.title}
                        </p>
                        <p className="mt-1 text-[11px] text-[#4f5d72] sm:text-[12px]">
                          {formatDateTime(item.time)}
                        </p>
                      </div>
                      <div className="col-span-2 pt-0 pl-8 text-[12px] leading-5 text-[#4f5d72] sm:col-span-1 sm:pt-0.5 sm:pl-0 sm:text-[13px]">
                        {item.detail}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-[42px] w-full items-center justify-center gap-2 rounded-[12px] border border-[#8ac393] bg-white px-5 text-[14px] font-semibold text-[#24472e] transition hover:bg-[#f6fff7] sm:h-[46px] sm:min-w-[150px] sm:w-auto sm:px-6 sm:text-[14px]"
                >
                  <FiX className="text-[16px]" />
                  Close
                </button>

                {normalizedOrderStatus === "delivered" && (
                  <button
                    type="button"
                    onClick={handleDownloadInvoice}
                    disabled={isDownloadingInvoice}
                    className="inline-flex h-[42px] w-full items-center justify-center gap-2 rounded-[12px] bg-[linear-gradient(180deg,#f8a100_0%,#f59b00_100%)] px-5 text-[14px] font-semibold text-white shadow-[0_16px_32px_rgba(245,158,11,0.22)] transition hover:brightness-105 disabled:cursor-wait disabled:opacity-75 sm:h-[46px] sm:min-w-[190px] sm:w-auto sm:px-6 sm:text-[14px]"
                  >
                    {isDownloadingInvoice ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white" />
                    ) : (
                      <FiDownload className="text-[15px]" />
                    )}
                    {isDownloadingInvoice
                      ? "Generating invoice..."
                      : "Download Invoice"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showCancelRestrictionDialog && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-md rounded-[20px] bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-[#1f2432]">
              Before You Cancel
            </h3>
            <p className="mt-3 text-sm leading-6 text-[#667085]">
              If you cancel this order then coupon used can&apos;t be used again
              in any new order and points used can&apos;t be refunded again.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancelRestrictionDialog(false)}
                className="rounded-[12px] border border-[#d8e0ea] px-4 py-2.5 text-sm font-semibold text-[#334155] transition hover:bg-[#f8fafc]"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCancelRestrictionDialog(false);
                  setShowCancelDialog(true);
                }}
                className="rounded-[12px] bg-[#f59b00] px-4 py-2.5 text-sm font-semibold text-white transition hover:brightness-105"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelDialog && orderDetails && (
        <CancelOrderDialog
          order={orderDetails}
          onConfirm={() => {
            setOrderDetails((current) => ({
              ...current,
              status: "cancelled",
            }));
            setShowCancelDialog(false);
            onOrderUpdated?.();
            onClose?.();
          }}
          onClose={() => setShowCancelDialog(false)}
        />
      )}
    </div>
  );
};

export default OrderDetails;
